import * as React from 'react';
import { useLocalStorage } from '@grandvisionhq/utils';

const DEFAULT_LIMIT = 20;
/**
 * Creates a history array in the local storage with some helpers attached.
 *
 * @param key - used as key in the local storage
 * @param limit - limit of items stored in the history array
 *
 * @example usage
 *  import { useViewHistory } from '@grandvisionhq/state'
 *  export const useProductViewHistory = useViewHistory.bind(null, ViewHistoryKey.Products, 30)
 */ function useViewHistory(key, limit = DEFAULT_LIMIT) {
    const [history, setHistory] = useLocalStorage(key, undefined);
    /**
   * Checks if an id exists in the current history
   */ const has = React.useCallback((id)=>{
        var ref;
        return Boolean(history && ((ref = history.find((i)=>i === id
        )) === null || ref === void 0 ? void 0 : ref.length));
    }, [
        history
    ]);
    /**
   * Adds one ID to the history array.
   * Duplicated IDs are not allowed.
   * New items are always added at the start of the history.
   * If the limit is exceeded, the last item(s) of the array are cut off.
   */ const add = React.useCallback((id)=>{
        setHistory((state = [])=>{
            var ref;
            const hasId = Boolean((ref = state.find((i)=>i === id
            )) === null || ref === void 0 ? void 0 : ref.length);
            const filtered = hasId ? [
                ...state.filter((i)=>i !== id
                )
            ] : [
                ...state
            ];
            return [
                id,
                ...filtered
            ].slice(0, limit);
        });
    }, [
        limit
    ]);
    /**
   * Removes one or more items from the history
   */ const remove = React.useCallback((ids)=>{
        setHistory((state = [])=>{
            const filters = Array.isArray(ids) ? ids : [
                ids
            ];
            return filters.reduce((result, next)=>[
                    ...result.filter((i)=>i !== next
                    )
                ]
            , [
                ...state
            ]);
        });
    }, []);
    /**
   * Clears the entire history array by restoring the initial value
   */ const clear = React.useCallback(()=>{
        setHistory([]);
    }, []);
    return {
        history,
        has,
        add,
        remove,
        clear
    };
}

export { DEFAULT_LIMIT, useViewHistory };
//# sourceMappingURL=use-view-history.js.map
